/**
 * NoteMention — inline node Tiptap: link către altă notiță din tenant.
 *
 * Persistat în HTML ca <a data-note-mention data-note-id="UUID">Titlu</a>.
 * NodeView React: chip brand-colored; click → navigate la /notes/<tenant>/<path>
 * (construit prin buildPath din /notes/{id}/path sau traversând lista locală).
 */
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { notesApi } from '@/api/notes';

export const NoteMentionNode = Node.create({
  name: 'noteMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      noteId: { default: '', parseHTML: (el) => el.getAttribute('data-note-id') },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-note-title') ?? el.textContent,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-note-mention]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'a',
      mergeAttributes({
        'data-note-mention': '',
        'data-note-id': HTMLAttributes.noteId,
        'data-note-title': HTMLAttributes.title,
        href: '#',
      }),
      node.attrs.title || 'notiță',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(NoteMentionView);
  },
});

function NoteMentionView({ node }: NodeViewProps) {
  const { t } = useTranslation();
  const noteId = node.attrs.noteId as string;
  const title = (node.attrs.title as string) || t('notes_blocks.mention.fallback_title');
  const navigate = useNavigate();

  // Cache path per-tenant; folosim endpoint-ul /notes/{id}/path
  // (lazy — doar la mount ca să avem URL corect chiar și dacă lista
  // de notițe n-a fost încă fetch-uită în contextul curent).
  const pathQuery = useQuery({
    queryKey: ['note-path', noteId],
    queryFn: () => notesApi.getPath(noteId),
    enabled: !!noteId,
    staleTime: 60_000,
  });

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pathQuery.data?.url) navigate(pathQuery.data.url);
  };

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      className="inline align-baseline"
    >
      <a
        href={pathQuery.data?.url ?? '#'}
        onClick={onClick}
        className="inline-flex items-center gap-1 text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 rounded px-1.5 py-0.5 text-sm hover:bg-brand-100 dark:hover:bg-brand-900/50 no-underline font-mono"
        title={pathQuery.data?.url ?? t('notes_blocks.mention.resolving')}
      >
        <span>📄</span>
        <span>{title}</span>
      </a>
    </NodeViewWrapper>
  );
}
